import Vue from 'vue';
import App from './App.vue';
import { dispatch, vuexStore } from './store';
import router from './router';
import VuePortal from '@linusborg/vue-simple-portal';
import SpatialNavigation from '@/plugins/spatial_navigation_plugin';
import Fragment from 'vue-fragment';

import 'intersection-observer';
import 'ress';
import '@/assets/sass/custom.scss';
import { smoothScroll } from './libs/smooth-scroll-into-view-if-needed';

import type { GooglePublisherTag } from './gpt';
import type firebaseType from 'firebase';

declare module 'vue/types/vue' {
  interface Vue {
    $analytics: firebaseType.analytics.Analytics;
  }
}

Vue.use(VuePortal);
Vue.config.productionTip = false;

Vue.use(SpatialNavigation, {
  keyMap: {
    up: 38,
    down: 40,
    left: 37,
    right: 39,
    enter: 13,
    back: 8,
  },

  scrollBehavior: (actions) => Promise.all(
    actions.reduce((results: Array<Promise<any>>, { el, left, top }) => {

      if (el.matches('.drawer-main-div')) {
        return results;
      }

      const newLeft = el.matches('.card-rail')
        ? left - (el.parentElement as HTMLElement).getBoundingClientRect().width
        : left;

      const startLeft = el.scrollLeft;
      const startTop = el.scrollTop;
      if (startLeft === newLeft && startTop === top) {
        return results;
      }

      return [
        ...results,
        new Promise((ok) => {
          return smoothScroll(
            el,
            newLeft,
            top,
            undefined,
            undefined,
            () => ok({
              el,
              left: [startLeft, newLeft],
              top: [startTop, top],
            } as any),
          );
        }),
      ];

    }, []),
  ),
});

Vue.use(Fragment.Plugin);

const main = async () => {

  import ('firebase').then(({ default: firebase }) => {
    const firebaseConfig = {
      apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
      authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
      projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.VUE_APP_FIREBASE_APP_ID,
      measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
    };

    // Initialize Firebase
    firebase.initializeApp(firebaseConfig);
    firebase.analytics();

    // alias
    Vue.prototype.$analytics = firebase.analytics();

    // Router analytics
    router.afterEach((to) => {
      firebase.analytics().setCurrentScreen(to.path, { screen_name: to.name ?? '' } as any as firebaseType.analytics.AnalyticsCallOptions);
      firebase.analytics().logEvent('page_view',
        {
          app_name: 'TV APP',
          screen_name: to.name ?? '',
          page_location: to.fullPath,
        },
      );
    });
  });

  await Promise.all([
    dispatch('fetchCountryCode'),
    dispatch('fetchNavigation'),
    dispatch('fetchCountries'),
  ]);

  new Vue({
    router,
    store: vuexStore,
    render: (h) => h(App),
  }).$mount('#app');
};

main();

export const gptInit = async () => {
  let gpt: GooglePublisherTag;

  const init = async () => {
    if (gpt !== undefined) { return gpt; }

    gpt = new (await import('@/gpt')).GooglePublisherTag({
      gptID: '',
    });

    return gpt;
  };

  return init();
};
